import { useState, useMemo } from 'react';
import { Search, CheckCircle2, XCircle, AlertTriangle, Clock, CircleDot, Minus } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { AdminTabShell } from './AdminTabShell';
import {
  type OnboardingChecklistRow,
  type OnboardingChecklistData,
} from '@/hooks/admin/useAdminOnboardingChecklist';
import {
  type OnboardingStatus,
  type ChecklistItemStatus,
} from '@/lib/onboarding-checklist';

// ─── Props ──────────────────────────────────────────────────────────────

interface OnboardingChecklistTabProps {
  data: OnboardingChecklistData | null;
  isLoading: boolean;
  error: Error | null;
}

// ─── Status config ──────────────────────────────────────────────────────

const STATUS_LABELS: Record<OnboardingStatus, string> = {
  not_started: 'Not Started',
  connected_no_data: 'Connected (No Data)',
  data_flowing: 'Data Flowing',
  needs_configuration: 'Needs Config',
  needs_verification: 'Needs Verification',
  report_ready: 'Report Ready',
  production_ready: 'Production Ready',
  blocked: 'Blocked',
};

const STATUS_COLORS: Record<OnboardingStatus, string> = {
  not_started: 'bg-zinc-800/50 text-zinc-400',
  connected_no_data: 'bg-blue-900/50 text-blue-300',
  data_flowing: 'bg-sky-900/50 text-sky-300',
  needs_configuration: 'bg-amber-900/50 text-amber-300',
  needs_verification: 'bg-orange-900/50 text-orange-300',
  report_ready: 'bg-teal-900/50 text-teal-300',
  production_ready: 'bg-emerald-900/50 text-emerald-300',
  blocked: 'bg-red-900/50 text-red-300',
};

const ITEM_STATUS_ICONS: Record<ChecklistItemStatus, typeof CheckCircle2> = {
  complete: CheckCircle2,
  incomplete: CircleDot,
  warning: AlertTriangle,
  blocked: XCircle,
  not_applicable: Minus,
};

const ITEM_STATUS_COLORS: Record<ChecklistItemStatus, string> = {
  complete: 'text-emerald-400',
  incomplete: 'text-zinc-500',
  warning: 'text-amber-400',
  blocked: 'text-red-400',
  not_applicable: 'text-zinc-600',
};

// ─── Filter type ────────────────────────────────────────────────────────

type StatusFilter = 'all' | OnboardingStatus;

// ─── Detail Panel ───────────────────────────────────────────────────────

function OnboardingDetailPanel({
  row,
  onClose,
}: {
  row: OnboardingChecklistRow;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-y-0 right-0 w-full max-w-lg bg-wt-bg-primary border-l border-wt-border shadow-xl z-50 overflow-y-auto">
      <div className="p-5 space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-sm font-semibold text-wt-text-primary">{row.property_name}</h2>
            <p className="text-xs text-wt-text-muted mt-0.5">
              {row.provider_type} {row.org_name ? `· ${row.org_name}` : ''}
            </p>
          </div>
          <button onClick={onClose} className="text-wt-text-muted hover:text-wt-text-primary text-lg leading-none">
            &times;
          </button>
        </div>

        {/* Status banner */}
        <div className={`rounded-lg p-3 text-xs ${STATUS_COLORS[row.overall_status]}`}>
          <div className="flex items-center justify-between">
            <span className="font-medium">{STATUS_LABELS[row.overall_status]}</span>
            <span className="font-mono">{row.completion_pct}%</span>
          </div>
          <CompletionBar pct={row.completion_pct} />
        </div>

        {/* Checklist items */}
        <div className="space-y-2">
          <h3 className="text-[11px] text-wt-text-muted uppercase tracking-wider font-medium">Checklist</h3>
          {row.items.map((item) => {
            const Icon = ITEM_STATUS_ICONS[item.status];
            return (
              <div
                key={item.key}
                className="bg-wt-bg-secondary rounded-lg p-2.5 border border-wt-border"
              >
                <div className="flex items-start gap-2">
                  <Icon className={`h-3.5 w-3.5 mt-0.5 shrink-0 ${ITEM_STATUS_COLORS[item.status]}`} />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium text-wt-text-primary">{item.label}</p>
                    <p className="text-[11px] text-wt-text-muted mt-0.5">{item.explanation}</p>
                    {item.recommended_action && (
                      <p className="text-[11px] text-wt-accent mt-1">{item.recommended_action}</p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── Main Tab ───────────────────────────────────────────────────────────

export function OnboardingChecklistTab({ data, isLoading, error }: OnboardingChecklistTabProps) {
  const [filter, setFilter] = useState<StatusFilter>('all');
  const [search, setSearch] = useState('');
  const [selectedRow, setSelectedRow] = useState<OnboardingChecklistRow | null>(null);

  const filtered = useMemo(() => {
    if (!data) return [];
    let list = data.rows;

    if (filter !== 'all') {
      list = list.filter((r) => r.overall_status === filter);
    }

    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((r) =>
        r.property_name.toLowerCase().includes(q) ||
        r.provider_type.toLowerCase().includes(q) ||
        (r.org_name?.toLowerCase().includes(q) ?? false)
      );
    }

    return list;
  }, [data, filter, search]);

  return (
    <AdminTabShell isLoading={isLoading} error={error}>
      {data && (
        <div className="space-y-4">
          {/* Summary cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <SummaryCard label="Production Ready" count={data.summary.production_ready} className="text-emerald-400" />
            <SummaryCard label="Report Ready" count={data.summary.report_ready} className="text-teal-400" />
            <SummaryCard label="Needs Config" count={data.summary.needs_configuration + data.summary.needs_verification} className="text-amber-400" />
            <SummaryCard label="Blocked" count={data.summary.blocked} className={data.summary.blocked > 0 ? 'text-red-400' : 'text-zinc-500'} />
            <SummaryCard label="Avg Completion" count={0} className="text-blue-400" customValue={`${data.summary.avg_completion_pct}%`} />
          </div>

          {/* Filters + search */}
          <div className="flex flex-wrap items-center gap-2">
            <FilterPill label="All" value="all" current={filter} onSelect={setFilter} />
            <FilterPill label="Blocked" value="blocked" current={filter} onSelect={setFilter} />
            <FilterPill label="Not Started" value="not_started" current={filter} onSelect={setFilter} />
            <FilterPill label="No Data" value="connected_no_data" current={filter} onSelect={setFilter} />
            <FilterPill label="Flowing" value="data_flowing" current={filter} onSelect={setFilter} />
            <FilterPill label="Needs Config" value="needs_configuration" current={filter} onSelect={setFilter} />
            <FilterPill label="Verification" value="needs_verification" current={filter} onSelect={setFilter} />
            <FilterPill label="Report Ready" value="report_ready" current={filter} onSelect={setFilter} />
            <FilterPill label="Production" value="production_ready" current={filter} onSelect={setFilter} />

            <div className="ml-auto relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-wt-text-muted" />
              <Input
                placeholder="Search property..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-7 pl-7 w-48 text-xs bg-wt-bg-secondary border-wt-border"
              />
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-wt-text-muted text-left border-b border-wt-border">
                  <th className="pb-2 pr-3 font-medium">Property</th>
                  <th className="pb-2 pr-3 font-medium">Provider</th>
                  <th className="pb-2 pr-3 font-medium">Status</th>
                  <th className="pb-2 pr-3 font-medium text-right">Completion</th>
                  <th className="pb-2 pr-3 font-medium text-right">Items Done</th>
                  <th className="pb-2 pr-3 font-medium">Next Action</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((row) => {
                  const applicable = row.items.filter((i) => i.status !== 'not_applicable');
                  const complete = applicable.filter((i) => i.status === 'complete').length;
                  const firstAction = row.items.find((i) =>
                    i.status === 'blocked' || i.status === 'incomplete' || i.status === 'warning'
                  );

                  return (
                    <tr
                      key={row.property_id}
                      className="border-b border-wt-border hover:bg-wt-bg-secondary/50 cursor-pointer transition-colors"
                      onClick={() => setSelectedRow(row)}
                    >
                      <td className="py-2 pr-3">
                        <span className="text-wt-text-primary font-mono text-[11px]">
                          {row.property_name}
                        </span>
                        {row.org_name && (
                          <span className="text-wt-text-muted ml-1.5 text-[10px]">{row.org_name}</span>
                        )}
                      </td>
                      <td className="py-2 pr-3 text-wt-text-secondary font-mono text-[11px]">
                        {row.provider_type}
                      </td>
                      <td className="py-2 pr-3">
                        <StatusBadge status={row.overall_status} />
                      </td>
                      <td className="py-2 pr-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <div className="w-16">
                            <CompletionBar pct={row.completion_pct} />
                          </div>
                          <span className="font-mono text-wt-text-primary w-8 text-right">
                            {row.completion_pct}%
                          </span>
                        </div>
                      </td>
                      <td className="py-2 pr-3 text-right font-mono text-wt-text-muted">
                        {complete}/{applicable.length}
                      </td>
                      <td className="py-2 pr-3 text-wt-text-muted truncate max-w-[200px]">
                        {firstAction?.recommended_action ?? '—'}
                      </td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={6} className="py-8 text-center text-wt-text-muted">
                      No properties match the current filter.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Detail panel */}
      {selectedRow && (
        <OnboardingDetailPanel row={selectedRow} onClose={() => setSelectedRow(null)} />
      )}
    </AdminTabShell>
  );
}

// ─── Small components ───────────────────────────────────────────────────

function SummaryCard({ label, count, className, customValue }: { label: string; count: number; className?: string; customValue?: string }) {
  return (
    <div className="bg-wt-bg-secondary rounded-lg p-3 border border-wt-border">
      <p className="text-[11px] text-wt-text-muted uppercase tracking-wider">{label}</p>
      <p className={`text-2xl font-mono font-semibold mt-1 ${className ?? 'text-wt-text-primary'}`}>
        {customValue ?? count}
      </p>
    </div>
  );
}

function FilterPill<T extends string>({
  label, value, current, onSelect,
}: { label: string; value: T; current: T; onSelect: (v: T) => void }) {
  const active = value === current;
  return (
    <button
      onClick={() => onSelect(value)}
      className={`px-2 py-0.5 rounded text-[11px] transition-colors ${
        active
          ? 'bg-wt-accent/20 text-wt-accent border border-wt-accent/40'
          : 'bg-wt-bg-tertiary text-wt-text-muted border border-transparent hover:border-wt-border'
      }`}
    >
      {label}
    </button>
  );
}

function StatusBadge({ status }: { status: OnboardingStatus }) {
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${STATUS_COLORS[status]}`}>
      {STATUS_LABELS[status]}
    </span>
  );
}

function CompletionBar({ pct }: { pct: number }) {
  const color = pct >= 90 ? 'bg-emerald-500' : pct >= 60 ? 'bg-teal-500' : pct >= 30 ? 'bg-amber-500' : 'bg-red-500';
  return (
    <div className="h-1.5 rounded-full bg-wt-bg-tertiary overflow-hidden mt-1">
      <div className={`h-full rounded-full ${color} transition-all`} style={{ width: `${pct}%` }} />
    </div>
  );
}
